import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useBranch } from '../context/BranchContext';
import { useAuth } from '../context/AuthContext';
import { useServiceChargeEnabled } from './useServiceChargeEnabled';
import {
    todayStr,
    yesterdayStr,
    isTodayRange,
} from '../services/reportsApi';
import { useStoreTimeZone, getStoreTimeZone } from '../utils/dateTimeUtils';
import { resolveTokenRange, normalizeToken } from '../utils/reportPeriod';
import { getCurrencySymbol } from '../utils/currency';
import { TG_CARD_LABEL } from '../components/reports/components/TipsGratuity';
import { getSnapshotDetails, getLiveByChannelApi, getLiveStaffPerformanceApi, getLiveRefundSummaryApi, getLivePaymentsApi } from '../redux/reports/clickHouse/clickHouseApi';
import type { OrderSource } from '../redux/reports/clickHouse/clickHouseApi';
import { useRefundLogPeriod } from '../components/reports/refunds/useRefundLogPeriod';
import { REFUND_KPI_TOOLTIP, tipRefundsByCheckOwner } from '../components/reports/refunds/refundModel';

const findToken = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.length > 50) return val;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const findLocationId = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['locationId', 'location_id', 'locationID', 'locId', 'outletId']) {
        if (obj[key] && typeof obj[key] === 'string') return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findLocationId(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const unwrap = (res: any): any => {
    if (res == null) return res;
    const d = res.metaDataInfo?.data ?? res.content ?? res.data ?? res.result ?? res.response;
    return d != null && d !== res ? d : res;
};

const toNum = (v: any): number => (v != null && v !== '' ? Number(v) : 0);

// Delegates to the single GLOBAL-DP resolver (utils/reportPeriod). The long-range EOD
// presets (Last Month, Last 3/6 Months, This/Last Year) now live there too, so a range
// picked here resolves identically on every other report the shared cross-report filter
// carries it to — previously they resolved to null everywhere except EOD.
// Short-range presets stay anchored on the store-timezone anchor date (5 AM cutoff) —
// see getAnchorDate / DateProdIssue.md.
function getStartEndFromFilter(filter: string, today: string, yesterday: string, timeZoneCd: string = getStoreTimeZone()): { startDate: string; endDate: string } | null {
    if (normalizeToken(filter) === 'today') return { startDate: today, endDate: today };
    return resolveTokenRange(filter, timeZoneCd);
}

const TENDER_COLORS = ['#D23A3B', '#4A90D9', '#D97706', '#16A34A', '#8B5CF6', '#9CA3B8'];

/**
 * Build tender pie data from the ClickHouse payments list — the SAME feed and the same
 * shape Sales reads for its "Sales by Tender Type" KPI, so both screens name, order and
 * total the tenders identically (EODCH-TENDER).
 *
 * API shape: `[{ tenderType, tenderAmount, transactionCount }]` — one row per tender, no
 * card-level rows and no server-side percentage. `value` is therefore derived here, then
 * re-scaled to sum to exactly 100 by normalizeTenderPercentages at the render site.
 * Rows sharing a tenderType are still merged: the endpoint groups them, but merging costs
 * nothing and keeps a duplicated row from drawing two slices of the same tender.
 *
 * Output: [{ name, value (%), color, totalSales, orders }] for chart + breakdown
 * (avg ticket = totalSales/orders in UI).
 */
function buildTenderData(paymentList: any): any[] {
    const list = Array.isArray(paymentList) ? paymentList : [];
    const total = list.reduce((sum, row) => sum + toNum(row.tenderAmount), 0);
    if (total <= 0) return [];
    const byName: Record<string, any> = {};
    list.forEach((row: any, i: number) => {
        const name = String(row.tenderType ?? `Tender ${i + 1}`).trim() || 'Other';
        if (!byName[name]) byName[name] = { name, amount: 0, orders: 0, color: TENDER_COLORS[Object.keys(byName).length % TENDER_COLORS.length] };
        byName[name].amount += toNum(row.tenderAmount);
        byName[name].orders += toNum(row.transactionCount);
    });
    return Object.values(byName)
        .map((d) => ({
            name: d.name,
            value: total > 0 ? (d.amount / total) * 100 : 0,
            color: d.color,
            totalSales: d.amount,
            orders: d.orders,
        }))
        .filter((d) => d.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, 12);
}

/**
  * A by-channel row is FIRST PARTY when its `orderSource` is `'direct'`; every other value is
  * the marketplace platform's own name (`Grubhub`, `Zomato`, `GloriaFood`, …) and counts as
  * third party. `orderSource` is the contract, so it decides — the channel-NAME regex below is
  * only a fallback for a row that arrives without one, where the old legacy-shaped code had
  * nothing else to go on either.
  */
const isThirdPartyChannelName = (name: any): boolean =>
    /third.?party|3p|uber|ubereats|door.?dash|grub.?hub|postmates|swiggy|zomato|deliveroo|just.?eat|talabat|foodpanda/i.test(String(name || ''));

export const isDirectChannelRow = (row: any): boolean => {
    const src = String(row?.orderSource ?? '').trim().toLowerCase();
    if (src) return src === 'direct';
    // Untagged row — fall back to the channel name. A blank source is far likelier an
    // un-tagged in-house order than a marketplace one, so anything the regex does not
    // recognise stays first party.
    return !isThirdPartyChannelName(row?.channel);
};

/**
 * Build channel chart data from the ClickHouse by-channel rows (EODCH-CHANNEL).
 *
 * API shape: one row per (channel, orderSource) pair —
 * `[{ channel, orderSource, grandTotal, netSales, orderCount, tip, gratuity, … }]`.
 * `grandTotal` is the amount actually charged to the customer, which is what this card
 * reports; the same figure the Total Collected KPI above it reads from snapshot-details.
 *
 * Rows are grouped by `channel` and each one's `grandTotal` is added to the first- or
 * third-party side per `isDirectChannelRow`, so a channel served by both (an in-house
 * Delivery row and a marketplace Delivery row) draws one bar with two stacked series
 * instead of two bars with the same name.
 *
 * Output: [{ channel, maghil, thirdParty, directSales, thirdPartySales, totalSales }] —
 * unchanged, so the chart, the tooltip and the drill read exactly the keys they always did.
 */
function buildChannelData(channelList: any): any[] {
    const list = Array.isArray(channelList) ? channelList : [];
    const byChannel = new Map<string, any>();
    list.forEach((row: any) => {
        const channel = String(row?.channel ?? '—').trim() || '—';
        if (!byChannel.has(channel)) byChannel.set(channel, { channel, maghil: 0, thirdParty: 0 });
        const entry = byChannel.get(channel);
        const amount = toNum(row?.grandTotal);
        if (isDirectChannelRow(row)) entry.maghil += amount;
        else entry.thirdParty += amount;
    });
    return Array.from(byChannel.values()).map((d) => ({
        channel: d.channel,
        // Keep old keys for existing UI usage.
        maghil: d.maghil,
        thirdParty: d.thirdParty,
        // Add explicit names to mirror maghil-portal terminology.
        directSales: d.maghil,
        thirdPartySales: d.thirdParty,
        totalSales: d.maghil + d.thirdParty,
    }));
}

/**
 * Build funds table from EOD report rows.
 * API shape: content[] with date, maghilTotalSale, cashSale, otherSale, cardSale (processed/deposited if present).
 */
const REFUND_REASON_COLORS = ['#DC2626', '#D97706', '#D23A3B', '#9CA3B8', '#5A6178'];

/** Build refund reasons from voided summary, cancellation summary, or product insights. */
function buildRefundReasons(refundSummaryRows: any): any[] {
    const rows = Array.isArray(refundSummaryRows) ? refundSummaryRows : [];
    const totalAmount = rows.reduce((sum, r) => sum + toNum(r.cancelledAmt), 0);
    if (totalAmount <= 0) return [];
    return rows
        .map((r, i) => {
            const amount = toNum(r.cancelledAmt);
            return {
                reason: String(r.cancelReason ?? 'Unknown').trim() || 'Unknown',
                amount,
                pct: Number(((amount / totalAmount) * 100).toFixed(1)),
                color: REFUND_REASON_COLORS[i % REFUND_REASON_COLORS.length],
            };
        })
        .filter((r) => r.amount > 0)
        .sort((a, b) => b.amount - a.amount);
}

const fmtKpi = (n: number): string => (n < 0 ? '-' : '') + getCurrencySymbol() + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Hook: EOD Summary data with live vs historical branching.
 * When date range is today (startDate === endDate === today), uses live APIs for tender, refunds, net sales.
 * @param {string} datePeriod - "Today" | "Yesterday" | "7D" | "30D" | "This Month"
 * @param orderSource - the global sales-channel filter, omitted for "Overall sales".
 *   Passed ONLY to the ClickHouse `/sales/live/v2/*` calls below; the legacy REST feeds
 *   (`/sales/channel`, `/sales/payment`, `/sales/live/payment`) do not accept it.
 * @returns {{ data, loading, error, locationId, dateRange, isToday, refetch }}
 */
export function useEodSummaryData(datePeriod: string, orderSource?: OrderSource) {
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    // Service Charge line exists only for outlets that capture one (hidden, never zeroed).
    const scEnabled = useServiceChargeEnabled();
    const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? findLocationId(auth) ?? '', [auth, branch]);
    const token = useMemo(() => (auth ? findToken(auth) : null), [auth]);

    const storeTz = useStoreTimeZone();
    const today = todayStr(storeTz);
    const yesterday = yesterdayStr(storeTz);
    const dateRange = useMemo(() => {
        const range = getStartEndFromFilter(datePeriod, today, yesterday, storeTz);
        return range ?? { startDate: today, endDate: today };
    }, [datePeriod, today, yesterday, storeTz]);
    // Read "today" off the resolved range so the date picker's today (`custom:<today>:<today>`)
    // takes the same live path as the Today pill — see isTodayRange (ZAR-1762).
    const isToday = useMemo(
        () => isTodayRange(dateRange.startDate, dateRange.endDate, today),
        [dateRange.startDate, dateRange.endDate, today],
    );

    /*
     * REF-1 / REF-3 — the period's refund component vector, from the one shared accessor.
     * Real data off GET /sales/live/v2/refund-log — the same hook the REF-2 Refunds
     * drill-in uses, so EOD's netting and that page's own figures can never drift.
     */
    const { summary: refundSummary } = useRefundLogPeriod({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        source: orderSource === 'direct' ? 'direct' : orderSource === 'thirdparty' ? 'thirdparty' : 'overall',
    });

    const fetchIdRef = useRef(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // EODCH-API-AUDIT — only the seven feeds that reach a rendered pixel survive.
    // The legacy REST calls this hook used to make (getEodReports, getEodTaxesData,
    // getByDuration, getEmployeeStaffTipGratuity, getVoidedOrderSummary,
    // getVoidedDetailsSummary, getPaidVoidedOrders, getLiveNetSales) all built objects
    // no EOD surface reads any more — the Funds and Taxes tables fetch their own pages,
    // and the revenue-trend / staff-tips / void-log sections no longer exist.
    const [raw, setRaw] = useState<any>({
        liveRefundSummary: null,
        // EODCH-CHANNEL — ONE by-channel response, deliberately unscoped by `orderSource`,
        // serving both the Channel Revenue card/drill and the Tips & Gratuity channel panels.
        // It replaced the separate legacy `channel` (/sales/channel) and `tgByChannel` keys.
        byChannel: null,
        payment: null,
        tgSnapshot: null,
        tgStaff: null,
    });

    const fetchData = useCallback(() => {
        if (!locationId || !token) {
            setLoading(false);
            setRaw({});
            return;
        }
        const id = ++fetchIdRef.current;
        setLoading(true);
        setError(null);
        // ClickHouse date params — when the selected range is Today (preset OR a Custom pick of
        // today's date, per the shape-based `isToday` above), omit startDate/endDate so these hit
        // the live window instead of a dated one; pass them for historical ranges. Matches
        // Snapshot and Sales; see isTodayRange (ZAR-1762). Covers Gross / Net / Total Refunds
        // (snapshot-details + refund-summary) AND the three Tips & Gratuity feeds — the latter
        // were still sending dates on Today, which is why that panel read as empty.
        const chDates: { startDate?: string; endDate?: string } = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        // The global sales-channel filter rides along with the ClickHouse date params.
        // Omitted entirely for "Overall sales" — never sent as '' or 'overall'.
        const chOpts = { locationId, ...chDates, ...(orderSource ? { orderSource } : {}), token };

        const allPromises = [
            // Tender Mix chart + the "Tender Types" drill-in. EODCH-TENDER — the same
            // ClickHouse endpoint, params and response keys Sales reads for its "Sales by
            // Tender Type" KPI, replacing the legacy REST pair (`/sales/live/payment` on
            // Today, a paginated `/sales/payment?size=50` otherwise). One endpoint now
            // covers both windows — dates omitted on Today, sent for a historical range —
            // and it is channel-aware, so the Tender Mix card and its drill finally move
            // when the global sales-channel filter is switched.
            getLivePaymentsApi(chOpts).catch(() => null),
            // ZAR-1762 / TG-2 — the Tips & Gratuity card and drill-in read the same three
            // ClickHouse endpoints, with the same params, that Snapshot and Sales read.
            // They now also carry `orderSource` (via chOpts): EOD gained the global
            // sales-channel filter, so the card, the drill and the page it sits on all move
            // together. Before that filter existed this comment said the opposite — EOD had
            // no source toggle, so scoping these to one channel would have made the drill
            // disagree with its own page and with the identical drill on Snapshot.
            // /sales/summary carries none of these figures — no order counts, no channel or
            // staff attribution — which is why the KPI reads ClickHouse instead.
            // EODCH-API-AUDIT — snapshot-details and staff-perfomance also back the Sales
            // waterfall further down the page, which used to dispatch them a SECOND time;
            // it now reads `data.waterfall`, built from these same two responses.
            getSnapshotDetails(chOpts).catch(() => null),
            // EODCH-CHANNEL — one response feeding BOTH the Channel Revenue chart + its
            // "Channels" drill (replacing legacy /sales/channel) AND the Tips & Gratuity
            // drill's two channel panels, which used to make their own second call to this
            // same endpoint. It carries `orderSource` like every other ClickHouse call here
            // (EODCH-CHANNEL-2), so the server does the scoping for both consumers and the
            // two requests could never diverge anyway.
            getLiveByChannelApi(chOpts).catch(() => null),
            getLiveStaffPerformanceApi(chOpts).catch(() => null),
            // Single source of truth for the "Total Refunds" KPI, its drilldown reasons and
            // the waterfall's Refunds step — one row per cancelReason.
            getLiveRefundSummaryApi(chOpts).catch(() => null),
        ];

        Promise.all(allPromises)
            .then((results) => {
                if (id !== fetchIdRef.current) return; // stale fetch — discard
                const [payment, tgSnapshot, byChannel, tgStaff, liveRefundSummary] = results;
                setRaw({
                    liveRefundSummary: liveRefundSummary ?? null,
                    byChannel: byChannel ?? null,
                    payment,
                    tgSnapshot: tgSnapshot ?? null,
                    tgStaff: tgStaff ?? null,
                });
            })
            .catch((err) => { if (id === fetchIdRef.current) setError(err?.message ?? 'Failed to load EOD data'); })
            .finally(() => { if (id === fetchIdRef.current) setLoading(false); });
    }, [locationId, token, dateRange.startDate, dateRange.endDate, isToday, orderSource]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const data = useMemo(() => {
        // /sales/live/v2/refund-summary — one row per cancelReason ({ cancelReason, removedQty,
        // orderCount, cancelledAmt }). The "Total Refunds" KPI is the sum of cancelledAmt across
        // every row; the drilldown reasons list is built from the same rows below.
        const refundSummaryRawUnwrapped = unwrap(raw.liveRefundSummary);
        const refundSummaryList: any[] = Array.isArray(refundSummaryRawUnwrapped) ? refundSummaryRawUnwrapped : [];
        const refundAmount = refundSummaryList.reduce((sum, r) => sum + toNum(r.cancelledAmt), 0);

        // Total Collected / Net Sales stamp KPIs — same single source of truth as the Sales/
        // Snapshot module's Net Sales KPI: /sales/live/v2/snapshot-details, always dispatched
        // with startDate/endDate so it covers Today and historical alike (no isToday branch).
        // grandTotal is the amount actually charged to the customer (tax + tips + other fees
        // included), matching this card's own tooltip text; netSales is grandTotal less those.
        const tgSnap: any = unwrap(raw.tgSnapshot) ?? {};
        const gross = toNum(tgSnap.grandTotal);
        const net = toNum(tgSnap.netSales);

        // ZAR-1762 / TG-2 — everything the Tips & Gratuity card and its drill-in need, from
        // ClickHouse. One source for totals, denominators and the channel split, so the card,
        // the sub-header and both panels can never disagree with each other.
        const channelRaw = raw.byChannel;
        const channelList: any[] = Array.isArray(channelRaw) ? channelRaw : (channelRaw?.content ?? unwrap(channelRaw) ?? []);
        // EODCH-CHANNEL-2 — the by-channel call carries `orderSource` again, so these rows
        // arrive already scoped and the panels use them as they come. (Under "3rd party
        // sales" the T&G card is hidden altogether, so only the Direct case ever narrows.)
        const tgChannels: any[] = channelList;
        const tgStaffRaw = raw.tgStaff;
        const tgStaffRows: any[] = Array.isArray(tgStaffRaw) ? tgStaffRaw : (tgStaffRaw?.content ?? unwrap(tgStaffRaw) ?? []);
        // Null-check, not `||`: a genuine 0 (a period with no tips) is a real answer and must
        // not be mistaken for a missing one.
        const tgNum = (v: any, fallback: number) => (v == null ? fallback : toNum(v));

        const tgTips = toNum(tgSnap.tip);
        const gratuityTotal = toNum(tgSnap.gratuity);
        // ClickHouse computes the combined total itself; sum only if it is absent.
        const tipsAndGratuity = tgNum(tgSnap.tipAndGratuity, tgTips + gratuityTotal);

        // EODCH-API-AUDIT-2 — no `delta` on any of the four cards.
        //
        // The percentages came from /sales/summary (totalSalesPercentage, netSalesPercentage,
        // cancelledAmtPercentage, totalTipsPercentage, totalGratuityPercentage) and that call
        // is gone: it supplied no figure the screen displayed, and a `hideDelta` list left over
        // from before the unified date picker suppressed the percentages on every EOD preset
        // except `Yesterday` — and on every custom range and every stepper press, which is how
        // the date control is actually driven.
        //
        // It could not simply be un-suppressed either. Since GLOBAL-DP-2 the control resolves
        // its comparison basis to the same weekday in the PRIOR WEEK and states it ("vs Tue,
        // Aug 25"), while /sales/summary computes its percentage against its own, different
        // basis — so showing that number under that label would have been confidently wrong.
        //
        // Restoring deltas here means reading the ClickHouse comparison endpoint against the
        // basis the control advertises, the way Snapshot does — not re-adding this call.
        // Hidden under "3rd party sales" and the row reflows to three, mirroring the Sales
        // report, which drops its Gratuity KPI for the same reason: gratuity is not a
        // meaningful figure for marketplace orders. Overall and Direct keep all four.
        const hideTipsGratuity = orderSource === 'thirdparty';

        // REF-3 — the total-cash-returned figure, same definition Snapshot/Sales use; falls
        // back to the gross ClickHouse figure while fixtures carry no data for the period.
        const refundKpiAmount = refundSummary.total > 0 ? refundSummary.total : refundAmount;
        const kpiItems = [
            // grandTotal already updates live when a fee is refunded, so no further netting
            // here — that would double-subtract the same refund.
            {
                label: 'Total Collected', value: fmtKpi(gross), icon: 'trending_up', iconColor: '#16A34A', blurColor: 'rgba(22,163,74,0.08)', drillKey: null,
                tooltip: 'Revenue deducting discounts, complimentary, voids & refunds. The amount paid by customers including tax, tips & other fees',
            },
            { label: 'Net Sales', value: fmtKpi(net), icon: 'account_balance_wallet', iconColor: '#D23A3B', blurColor: 'rgba(210,58,59,0.08)', drillKey: null, tooltip: 'From Total collected after deducting tax, tips & gratuity. The revenue retained by you after all deductions.' },
            // REF-3 — total cash returned across all components; only item refunds reduce Net Sales.
            {
                label: 'Total Refunds', value: fmtKpi(refundKpiAmount), drillKey: 'refunds', icon: 'undo', iconColor: '#DC2626', blurColor: 'rgba(220,38,38,0.08)',
                tooltip: REFUND_KPI_TOOLTIP,
                sub: refundKpiAmount > refundSummary.itemTotal ? `${fmtKpi(refundSummary.itemTotal)} reduced Net Sales` : undefined,
            },
            // Raw tipAndGratuity straight off snapshot-details — no refund netting.
            ...(hideTipsGratuity ? [] : [{
                label: TG_CARD_LABEL, value: fmtKpi(tipsAndGratuity), drillKey: 'tips', icon: 'volunteer_activism', iconColor: '#16A34A', blurColor: 'rgba(22,163,74,0.08)', cardBg: 'rgba(22,163,74,0.08)',
            }]),
        ];

        // EODCH-CHANNEL — same `channelList` the T&G panels above read, grouped by channel
        // and split first- / third-party on each row's own `orderSource`.
        const channelData = buildChannelData(channelList);
        const totalMaghil = channelData.reduce((s, r) => s + (r.maghil || 0), 0);
        const totalThirdParty = channelData.reduce((s, r) => s + (r.thirdParty || 0), 0);
        const channelTotal = totalMaghil + totalThirdParty;
        // EODCH-CHANNEL-2 — every figure below is computed over whatever rows the filter
        // let through, because the endpoint now does the scoping. The arithmetic is unchanged
        // and still reads each row's own `orderSource`; under Direct or 3rd party it simply
        // has only that side to work with, so Total Sales equals the one populated side,
        // the other card reads 0 and Maghil % pins to 100% / 0%. That is the same page-wide
        // scope every other KPI on the screen already follows.
        const channelSummary = {
            totalSales: channelTotal,
            directSales: totalMaghil,
            thirdPartySales: totalThirdParty,
            totalMaghil,
            totalThirdParty,
            maghilPct: channelTotal > 0 ? (totalMaghil / channelTotal) * 100 : 0,
        };

        const paymentRaw = raw.payment;
        const paymentList = Array.isArray(paymentRaw) ? paymentRaw : (paymentRaw?.content ?? unwrap(paymentRaw) ?? []);
        const tenderData = buildTenderData(Array.isArray(paymentList) ? paymentList : []);

        const refundReasons = buildRefundReasons(refundSummaryList);

        // ZAR-1762 — feeds the Tips & Grat card and its drill-in, and nothing else. Same three
        // ClickHouse endpoints, same params and same response keys Snapshot and Sales use, so
        // the identical drill reads identically on both surfaces.
        const tipsGratuity = {
            totalTips: tgTips,
            gratuity: gratuityTotal,
            combined: tipsAndGratuity,
            // Same source as the totals above, so both rates divide by the Net Sales those
            // totals came from — and match the identical drill on Snapshot.
            netSales: net,
            // Tips averages over ALL orders, Gratuity over gratuity-bearing orders only (TG-2).
            totalOrders: toNum(tgSnap.orderCount),
            ordersWithTips: toNum(tgSnap.ordersWithTips),
            ordersWithGratuity: toNum(tgSnap.ordersWithGratuity),
            channels: tgChannels.map((c: any) => ({
                channel: c?.channel ?? null,
                tip: toNum(c?.tip),
                gratuity: toNum(c?.gratuity),
            })),
            // Rankings from staff-perfomance — the feed Snapshot and Sales rank from, and the
            // only one carrying `staff_name`.
            staff: tgStaffRows.map((r: any) => ({
                name: r?.staff_name ?? '',
                tip: toNum(r?.tip),
                gratuity: toNum(r?.gratuity),
                orderCount: toNum(r?.orderCount),
                netSales: toNum(r?.netSales),
            })),
        };

        // EODCH-API-AUDIT — the ZAR-1462 Sales waterfall's own share of the ladder.
        // Every field here comes from a response this hook has ALREADY fetched
        // (snapshot-details, staff-perfomance, refund-summary), so EodSalesWaterfall no
        // longer re-dispatches those three endpoints for itself. It still owns the two
        // feeds nothing else on the page uses — void-summary and comp-order-log.
        // Same arithmetic as before: refunds is the summed cancelledAmt, tips the summed
        // per-staff tip, the rest read straight off snapshot-details.
        const waterfall = {
            discounts: toNum(tgSnap.discount),
            refunds: refundAmount,
            netSales: net,
            tax: toNum(tgSnap.tax),
            tips: tgStaffRows.reduce((sum: number, r: any) => sum + toNum(r?.tip), 0),
            gratuity: toNum(tgSnap.gratuity),
            otherCharges: toNum(tgSnap.otherCharges),
            totalCollected: gross,
            // Service Charge — same snapshot-details response, same contract as Snapshot/Sales.
            serviceCharge: scEnabled ? toNum(tgSnap.serviceFee) : null,
            serviceChargeRetained: scEnabled && !!tgSnap.serviceChargeRetained,
            // REF-1 — item vs fee refund components off the same snapshot-details response,
            // for the waterfall's Refunds/Others rows.
            itemRefund: toNum(tgSnap.refund),
            feeRefund: toNum(tgSnap.feeRefund),
        };

        const refundsSummary = {
            refundAmount,
            refundRate: gross > 0 ? (refundAmount / gross) * 100 : 0,
        };

        return {
            kpiItems,
            tenderData,
            channelData,
            channelSummary,
            refundReasons,
            tipsGratuity,
            refundsSummary,
            waterfall,
            // REF-6 — feeds the Staff Tips ranking's netting + tip-out exposure icon.
            tipRefundsByCheckOwner: tipRefundsByCheckOwner(refundSummary.records),
        };
    }, [raw, orderSource, scEnabled, refundSummary]);

    return {
        data,
        loading,
        error,
        locationId,
        dateRange,
        isToday,
        refetch: fetchData,
    };
}
